import Button, { BUTTON_VARIANTS } from '@/atoms/Button';
import { SIZE_TO_CLASS } from '@/atoms/Button/const';
import CardBadge, { CARD_BADGE_VARIANT } from '@/atoms/CardBadge';
import ToolTip from '@/atoms/ToolTip';
import CardCTATools from '@/organisms/CardCTATools';
import { HYSTORY_TYPE_TO_ICON, HYSTORY_TYPE_TO_VARIANT } from '@/organisms/CardHistory/HistoryToIcon';
import ListOffer from '@/organisms/ListOffer';
import { useMediaQuery } from '@react-hookz/web';
import clx from 'classnames';
import ModalSimpleLayout from 'context/Modal/layout/Simple';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LinkType, SIZE, VARIANT_COLOR_ENUM } from 'types/utils';
import { useCmsData } from 'utils/cms';
import Image from 'utils/Image';
import { applyClassesForBreakpoint } from 'utils/styles';
import useZoom from './hooks';
import ZoomSliderLegacy from './ZoomSliderLegacy';

const MotionImage = motion(Image);

export type ZoomSliderProps = {
  closeModal: () => void;
  cardRef: string;
  linkProps?: LinkType;
  extraElement?: (card: ReturnType<typeof useZoom>['card']) => JSX.Element;
  hideDetailLink?: boolean;
};

const ZoomSliderV2 = ({ closeModal, linkProps, cardRef, extraElement, hideDetailLink }: ZoomSliderProps) => {
  const { t } = useTranslation();
  const { card, handleNext, handlePrev, currentIndex, swipeHandler, cardsNumber } = useZoom(cardRef);
  const { generalSettings } = useCmsData();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const href = linkProps?.href || `/cards/${card?.reference}`;
  const label = linkProps?.label || 'see-details';

  return (
    <ModalSimpleLayout closeModal={closeModal} onOpenAutoFocus={(e) => e.preventDefault()}>
      <div
        className={`
          relative w-full sm:w-3/4 h-full md:h-auto max-h-full mx-2
          flex flex-col items-center justify-around py-2 md:pb-2 pb-12
          focus:outline-none !pointer-events-none
        `}
      >
        {!!cardsNumber && (
          <>
            <Button
              className={clx(
                'absolute left-4 z-1 md:left-0 top-0 bottom-0 my-auto h-fit pointer-events-auto',
                applyClassesForBreakpoint('md', SIZE_TO_CLASS[SIZE.LG]),
              )}
              disabled={currentIndex! === 0}
              onClick={handlePrev}
              size={SIZE.SM}
              tabIndex={-1}
            >
              <i className="fa-solid fa-arrow-left-long text-base" />
            </Button>
            <Button
              className={clx(
                'absolute right-4 z-1 md:right-0 top-0 bottom-0 my-auto h-fit pointer-events-auto',
                applyClassesForBreakpoint('md', SIZE_TO_CLASS[SIZE.LG]),
              )}
              disabled={cardsNumber === (currentIndex || 0) + 1}
              onClick={handleNext}
              size={SIZE.SM}
              tabIndex={-1}
            >
              <i className="fa-solid fa-arrow-right-long text-base" />
            </Button>
          </>
        )}
        {card?.reference && <CardCTATools className="pointer-events-auto shrink-0" reference={card?.reference} />}

        <div className="md:max-h-[90vh] overflow-auto md:overflow-hidden p-4 md:pb-4">
          <div className="flex gap-2 lg:gap-8 lg:flex-row flex-col self-center">
            <figure
              {...swipeHandler}
              className={clx(
                'rounded-lg relative pointer-events-auto z-0',
                'h-[334px] w-[240px] md:h-[404px] md:w-[290px] m-auto',
              )}
            >
              {card?.imagePath && (
                <AnimatePresence initial={false}>
                  <MotionImage
                    key={`${card?.imagePath} ${card.reference}`}
                    alt={card?.name || card?.reference || ''}
                    animate={{ opacity: 1, scale: 1, zIndex: 1, x: '0' }}
                    className={clx('rounded-lg absolute top-0 left-0 right-0 m-auto z-1 max-h-full max-w-full')}
                    exit={{ opacity: 0, scale: 1.05, zIndex: 0, x: '-1.5rem', transformOrigin: 'right' }}
                    initial={{ opacity: 0, scale: 1.05, zIndex: 1, x: '1.5rem' }}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    src={card.imagePath}
                    transition={{ ease: 'easeInOut' }}
                    fill
                  />
                  <div className="absolute right-2 bottom-2 z-3 flex flex-col gap-1">
                    {'isSuspended' in card && card.isSuspended && (
                      <ToolTip value="suspended">
                        <CardBadge
                          icon={HYSTORY_TYPE_TO_ICON.SUSPENDED}
                          variant={HYSTORY_TYPE_TO_VARIANT.SUSPENDED as keyof typeof CARD_BADGE_VARIANT}
                        />
                      </ToolTip>
                    )}

                    {'stats' in card && card.stats ? (
                      <>
                        {typeof card.stats.inMyCollection !== 'undefined' && !card.stats.isExclusive && (
                          <ToolTip value="copies-I-own">
                            <CardBadge icon="fa-solid fa-book-sparkles" variant={VARIANT_COLOR_ENUM.NEUTRAL}>
                              {card.stats.inMyCollection}
                            </CardBadge>
                          </ToolTip>
                        )}
                        {typeof card.stats.numberCopyAvailable !== 'undefined' && card.stats.isExclusive && (
                          <ToolTip value="exclusive-card">
                            <CardBadge icon="fa-solid fa-stars" variant={VARIANT_COLOR_ENUM.NEUTRAL}>
                              {`${card.stats.numberCopyAvailable}/${card.stats.inMyCollection}`}
                            </CardBadge>
                          </ToolTip>
                        )}
                      </>
                    ) : (
                      <ToolTip value="loading">
                        <CardBadge
                          icon="fa-solid fa-circle-notch animate-spin"
                          size={SIZE.MD}
                          variant={VARIANT_COLOR_ENUM.NEUTRAL}
                        />
                      </ToolTip>
                    )}

                    {generalSettings.marketplace && (
                      <ToolTip value="copies-I-sell">
                        <CardBadge
                          icon={'stats' in card ? 'fa-solid fa-store' : 'fa-solid fa-circle-notch animate-spin'}
                          size={SIZE.MD}
                        >
                          {('stats' in card && card.stats?.inMySale) || 0}
                        </CardBadge>
                      </ToolTip>
                    )}
                  </div>
                </AnimatePresence>
              )}
            </figure>
            <div className="flex justify-center items-center">
              {card?.reference && <ListOffer cardRef={card.reference} itemsPerPage={isDesktop ? 3 : 2} />}
            </div>
          </div>
          {!!card && card.reference && extraElement && (
            <div className="pt-2 md:pt-4 pointer-events-auto flex justify-center">{extraElement(card)}</div>
          )}
        </div>
        {(linkProps?.href || card?.reference) && !hideDetailLink && (
          <div className="pointer-events-auto lg:my-4 fixed md:static m-auto bottom-4 md:bottom-20">
            <Button href={href} variant={BUTTON_VARIANTS.PRIMARY}>
              {t(label)}
            </Button>
          </div>
        )}
      </div>
    </ModalSimpleLayout>
  );
};

const ZoomSlider = ({ closeModal, linkProps, cardRef, extraElement, hideDetailLink }: ZoomSliderProps) => {
  const { generalSettings } = useCmsData();

  switch (generalSettings.marketplace) {
    case true:
      return (
        <ZoomSliderV2
          cardRef={cardRef}
          closeModal={closeModal}
          extraElement={extraElement}
          hideDetailLink={hideDetailLink}
          linkProps={linkProps}
        />
      );
    default:
      return (
        <ZoomSliderLegacy cardRef={cardRef} closeModal={closeModal} extraElement={extraElement} linkProps={linkProps} />
      );
  }
};

export default ZoomSlider;
